/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Drawer, Box, Stack, Typography, IconButton, Autocomplete, TextField,
    Chip, Avatar, CircularProgress, Button, Checkbox, LinearProgress,
    Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import Iconify from '../Iconify';
import DentallyAvailabilityResults from './DentallyAvailabilityResults';
import { fetchDentallyAvailability } from '../../utils/dentallyApi';
import { availPractitionerName, parseDentallyDate } from '../../utils/dentallyUtils';
import useDentallyReasonOptions from '../../hooks/useDentallyReasonOptions';
import { useSelector } from '../../redux/store';
import {
    drawerPaperStyle,
    drawerHeaderStyle,
    drawerHeaderIconStyle,
    drawerBodyStyle,
    sectionCardStyle,
    sectionLabelStyle,
    presetChipGroupStyle,
    presetChipStyle,
    searchButtonStyle,
    searchProgressBarStyle,
    resultsHeaderStyle,
    drawerFooterStyle,
    footerSummaryStyle,
    confirmButtonStyle,
    practitionerOptionStyle,
    practitionerTagChipStyle,
    practitionerOverflowChipStyle,
    selectAllRowStyle,
} from '../../styles/DentallyBookingStyle';

const TIMEFRAME_PRESETS = [
    { key: '1day', label: 'Next 1 Day', days: 1, icon: 'mdi:clock-outline' },
    { key: '2days', label: 'Next 2 Days', days: 2, icon: 'mdi:calendar-today' },
    { key: '7days', label: 'Next 7 Days', days: 7, icon: 'mdi:calendar-week' },
    { key: '14days', label: 'Next 14 Days', days: 14, icon: 'mdi:calendar-month' },
    { key: 'custom', label: 'Custom', days: null, icon: 'mdi:calendar-edit' },
];

const DURATION_PRESETS = [
    { key: 15, label: '15 min' },
    { key: 30, label: '30 min' },
    { key: 45, label: '45 min' },
    { key: 60, label: '1 hour' },
    { key: 90, label: '1.5 hrs' },
    { key: 'custom', label: 'Custom' },
];

// Split a large availability window into discrete time slots of the requested duration.
// Uses parseDentallyDate to handle DD-MM-YYYY format from the Dentally API.
const splitSlotsByDuration = (slots, durationMinutes) => {
    if (!durationMinutes || durationMinutes <= 0) return slots;
    const now = Date.now();
    const result = [];
    slots.forEach((slot) => {
        const startDate = parseDentallyDate(slot.start_time);
        const endDate = parseDentallyDate(slot.finish_time);
        if (!startDate || !endDate) return; // skip unparseable slots
        const start = startDate.getTime();
        const end = endDate.getTime();
        if (end <= now) return; // skip past slots entirely
        const windowMinutes = (end - start) / 60000;

        // Slot already fits — normalize to ISO
        if (windowMinutes <= durationMinutes) {
            result.push({ ...slot, start_time: startDate.toISOString(), finish_time: endDate.toISOString() });
            return;
        }

        // Generate non-overlapping sub-slots, skip sub-slots in the past
        const step = durationMinutes * 60000;
        let cursor = Math.max(start, now);
        if (cursor > start) {
            const elapsed = cursor - start;
            cursor = start + Math.ceil(elapsed / step) * step;
        }
        while (cursor + step <= end) {
            result.push({
                ...slot,
                start_time: new Date(cursor).toISOString(),
                finish_time: new Date(cursor + step).toISOString(),
            });
            cursor += step;
        }
    });
    return result;
};

const DentallyAvailabilityDrawer = ({ open, onClose, onSelect, practiceId }) => {
    const { practitioners: allPractitioners, isLoading: practLoading } = useSelector((state) => state.dentallyPractitioners);
    const { reasons: reasonOptions, reasonTreatmentMap } = useDentallyReasonOptions(practiceId);

    // Filter to only active practitioners, deduplicate by ID
    const activePractitioners = useMemo(() => {
        const seen = new Set();
        return (allPractitioners || []).filter((p) => {
            if (p.active === false || seen.has(p.id)) return false;
            seen.add(p.id);
            return true;
        });
    }, [allPractitioners]);

    // Form state
    const [selectedPractitioners, setSelectedPractitioners] = useState([]);
    const [timeframePreset, setTimeframePreset] = useState('7days');
    const [customStartDate, setCustomStartDate] = useState(null);
    const [customEndDate, setCustomEndDate] = useState(null);
    const [durationPreset, setDurationPreset] = useState(30);
    const [customDuration, setCustomDuration] = useState('');

    // Results state
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchProgress, setSearchProgress] = useState({ done: 0, total: 0 });
    const [hasSearched, setHasSearched] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [appointmentReason, setAppointmentReason] = useState('');

    const getTimeRange = useCallback(() => {
        if (timeframePreset === 'custom') {
            return {
                start: customStartDate ? customStartDate.toISOString() : new Date().toISOString(),
                end: customEndDate ? customEndDate.toISOString() : new Date().toISOString(),
            };
        }
        const preset = TIMEFRAME_PRESETS.find(p => p.key === timeframePreset);
        const now = new Date();
        const end = new Date(now);
        end.setDate(end.getDate() + (preset?.days || 7));
        return { start: now.toISOString(), end: end.toISOString() };
    }, [timeframePreset, customStartDate, customEndDate]);

    const getDuration = useCallback(() => {
        if (durationPreset === 'custom') return customDuration ? Number(customDuration) : null;
        return durationPreset;
    }, [durationPreset, customDuration]);

    // Fire one API call per practitioner in parallel, merge all results
    const handleSearch = useCallback(async () => {
        if (!practiceId || selectedPractitioners.length === 0) return;

        const { start, end } = getTimeRange();
        if (!start || !end) return;

        setIsSearching(true);
        setHasSearched(true);
        setResults([]);
        setSelectedSlot(null);

        const duration = getDuration();
        const total = selectedPractitioners.length;
        let done = 0;
        setSearchProgress({ done: 0, total });

        // Build a name lookup from selectedPractitioners so we can tag each slot
        const nameMap = {};
        selectedPractitioners.forEach((p) => {
            nameMap[p.id] = availPractitionerName(p);
        });

        try {
            const promises = selectedPractitioners.map((pract) =>
                fetchDentallyAvailability(practiceId, {
                    startTime: start,
                    finishTime: end,
                    practitionerIds: [pract.id],
                    duration,
                    perPage: 50,
                    page: 1,
                }).then((res) => {
                    done += 1;
                    setSearchProgress({ done, total });
                    const slots = (res?.ok || res?.status === 200)
                        ? (res.data?.availability || [])
                        : [];
                    // Tag each slot with the practitioner we queried for
                    return (Array.isArray(slots) ? slots : []).map((s) => ({
                        ...s,
                        _queried_practitioner_id: pract.id,
                        _queried_practitioner_name: nameMap[pract.id] || availPractitionerName(pract),
                    }));
                }).catch(() => {
                    done += 1;
                    setSearchProgress({ done, total });
                    return [];
                })
            );

            const allResults = await Promise.all(promises);
            const merged = allResults.flat();
            // Split large availability windows into precise duration-sized time slots
            const sliced = splitSlotsByDuration(merged, duration);
            // Sort by start_time ascending
            sliced.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
            setResults(sliced);
        } catch {
            setResults([]);
        } finally {
            setIsSearching(false);
        }
    }, [practiceId, selectedPractitioners, getTimeRange, getDuration]);

    const handleSlotSelect = (slot) => {
        setSelectedSlot(slot);
    };

    const handleConfirmSelection = () => {
        if (selectedSlot) {
            onSelect({
                ...selectedSlot,
                reason: appointmentReason,
                // Pass linked treatment name so parent can auto-fill
                reasonLinkedTreatment: reasonTreatmentMap[appointmentReason] || null,
                notes: 'Appointment created from DentalSem CRM',
            });
            handleReset();
            onClose();
        }
    };

    const handleReset = () => {
        setSelectedPractitioners([]);
        setTimeframePreset('7days');
        setCustomStartDate(null);
        setCustomEndDate(null);
        setDurationPreset(30);
        setCustomDuration('');
        setResults([]);
        setHasSearched(false);
        setSelectedSlot(null);
        setAppointmentReason('');
        setSearchProgress({ done: 0, total: 0 });
    };

    const allSelected = activePractitioners.length > 0 && selectedPractitioners.length === activePractitioners.length;

    const handleToggleSelectAll = () => {
        if (allSelected) {
            setSelectedPractitioners([]);
        } else {
            setSelectedPractitioners([...activePractitioners]);
        }
    };

    const canSearch = selectedPractitioners.length > 0 && (timeframePreset !== 'custom' || (customStartDate && customEndDate));

    const progressPercent = searchProgress.total > 0
        ? Math.round((searchProgress.done / searchProgress.total) * 100)
        : 0;

    return (
        <Drawer
            anchor='right'
            open={open}
            onClose={onClose}
            PaperProps={{ sx: drawerPaperStyle }}
            style={{ zIndex: 1400 }}
        >
            {/* ─── Header ─── */}
            <Box sx={drawerHeaderStyle}>
                <Stack direction='row' alignItems='center' spacing={1.5}>
                    <Box sx={drawerHeaderIconStyle}>
                        <Iconify icon='mdi:calendar-search' width={20} sx={{ color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant='subtitle1' sx={{ fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.2px' }}>
                            Dentally Availability
                        </Typography>
                        <Typography variant='caption' sx={{ color: 'text.disabled', fontSize: '11px' }}>
                            Search and book appointment slots
                        </Typography>
                    </Box>
                </Stack>
                <IconButton
                    size='small'
                    onClick={onClose}
                    sx={{
                        bgcolor: 'rgba(145,158,171,0.08)',
                        '&:hover': { bgcolor: 'rgba(145,158,171,0.16)' },
                    }}
                >
                    <Iconify icon='mdi:close' width={18} />
                </IconButton>
            </Box>

            {/* ─── Body ─── */}
            <Box sx={drawerBodyStyle}>

                {/* ─── Practitioner Selection Card ─── */}
                <Box sx={sectionCardStyle}>
                    <Typography sx={sectionLabelStyle}>
                        <Iconify icon='mdi:account-group-outline' width={15} sx={{ color: 'primary.main' }} />
                        Practitioners
                    </Typography>
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={activePractitioners}
                        getOptionLabel={(p) => availPractitionerName(p)}
                        value={selectedPractitioners}
                        onChange={(_, val) => setSelectedPractitioners(val)}
                        loading={practLoading}
                        filterOptions={(options, { inputValue }) => {
                            const q = inputValue.toLowerCase();
                            if (!q) return options;
                            return options.filter((p) => {
                                const u = p.user || {};
                                return [u.title, u.first_name, u.last_name, u.email, String(p.id)]
                                    .some((v) => v && String(v).toLowerCase().includes(q));
                            });
                        }}
                        renderOption={(props, p, { selected }) => {
                            const u = p.user || {};
                            const name = availPractitionerName(p);
                            const initials = ((u.first_name?.[0] || '') + (u.last_name?.[0] || '')).toUpperCase() || '?';
                            return (
                                <Box component='li' {...props} key={p.id} sx={practitionerOptionStyle}>
                                    <Checkbox
                                        size='small'
                                        checked={selected}
                                        sx={{ mr: 0.75, p: 0.5, color: 'text.disabled' }}
                                    />
                                    <Stack direction='row' alignItems='center' spacing={1}>
                                        <Avatar sx={{
                                            width: 28, height: 28, fontSize: 10, fontWeight: 700,
                                            bgcolor: p.colour || '#5C6BC0',
                                            boxShadow: `0 2px 4px ${p.colour || '#5C6BC0'}30`,
                                        }}>
                                            {initials}
                                        </Avatar>
                                        <Box>
                                            <Typography variant='body2' noWrap sx={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3 }}>
                                                {name}
                                            </Typography>
                                            {u.email && (
                                                <Typography variant='caption' sx={{ color: 'text.disabled', fontSize: 10 }}>
                                                    {u.email}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                </Box>
                            );
                        }}
                        renderTags={(tagValue, getTagProps) => {
                            const maxVisible = 3;
                            const visible = tagValue.slice(0, maxVisible);
                            const overflow = tagValue.length - maxVisible;
                            return (
                                <>
                                    {visible.map((p, index) => {
                                        const tagProps = getTagProps({ index });
                                        return (
                                            <Chip
                                                {...tagProps}
                                                key={p.id}
                                                label={availPractitionerName(p)}
                                                size='small'
                                                avatar={
                                                    <Avatar sx={{
                                                        width: 20, height: 20, fontSize: 9,
                                                        bgcolor: `${p.colour || '#5C6BC0'} !important`,
                                                        color: '#fff !important',
                                                    }}>
                                                        {((p.user?.first_name?.[0] || '') + (p.user?.last_name?.[0] || '')).toUpperCase() || '?'}
                                                    </Avatar>
                                                }
                                                sx={practitionerTagChipStyle}
                                            />
                                        );
                                    })}
                                    {overflow > 0 && (
                                        <Chip
                                            label={`+${overflow} more`}
                                            size='small'
                                            sx={practitionerOverflowChipStyle}
                                        />
                                    )}
                                </>
                            );
                        }}
                        ListboxComponent={React.forwardRef((listboxProps, ref) => (
                            <Box component='ul' ref={ref} {...listboxProps} sx={{ ...listboxProps.sx, maxHeight: 280, p: 0 }}>
                                <Box
                                    component='li'
                                    onClick={handleToggleSelectAll}
                                    sx={selectAllRowStyle(allSelected)}
                                >
                                    <Checkbox
                                        size='small'
                                        checked={allSelected}
                                        indeterminate={selectedPractitioners.length > 0 && !allSelected}
                                        sx={{ mr: 0.75, p: 0.5 }}
                                    />
                                    <Typography variant='body2' sx={{ fontWeight: 600, fontSize: 13 }}>
                                        Select All ({activePractitioners.length})
                                    </Typography>
                                </Box>
                                {listboxProps.children}
                            </Box>
                        ))}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size='small'
                                placeholder={selectedPractitioners.length === 0 ? 'Search practitioners...' : ''}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {practLoading && <CircularProgress size={16} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                        bgcolor: '#F8FAFC',
                                        '&.Mui-focused': { bgcolor: '#fff' },
                                    },
                                }}
                            />
                        )}
                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                        noOptionsText={practLoading ? 'Loading...' : 'No active practitioners found'}
                        size='small'
                    />
                    {selectedPractitioners.length > 0 && (
                        <Typography variant='caption' sx={{ color: 'text.disabled', mt: 0.75, display: 'block', fontSize: '11px' }}>
                            {selectedPractitioners.length} of {activePractitioners.length} practitioner{activePractitioners.length !== 1 ? 's' : ''} selected
                        </Typography>
                    )}
                </Box>

                {/* ─── Timeframe Card ─── */}
                <Box sx={sectionCardStyle}>
                    <Typography sx={sectionLabelStyle}>
                        <Iconify icon='mdi:calendar-range' width={15} sx={{ color: '#FFAB00' }} />
                        Timeframe
                    </Typography>
                    <Box sx={presetChipGroupStyle}>
                        {TIMEFRAME_PRESETS.map(({ key, label }) => (
                            <Chip
                                key={key}
                                label={label}
                                size='small'
                                variant={timeframePreset === key ? 'filled' : 'outlined'}
                                onClick={() => setTimeframePreset(key)}
                                sx={presetChipStyle(timeframePreset === key)}
                            />
                        ))}
                    </Box>
                    {timeframePreset === 'custom' && (
                        <Stack direction='row' spacing={1.5} sx={{ mt: 1.5 }}>
                            <DateTimePicker
                                label='Start'
                                value={customStartDate}
                                onChange={setCustomStartDate}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                minDateTime={new Date()}
                            />
                            <DateTimePicker
                                label='End'
                                value={customEndDate}
                                onChange={setCustomEndDate}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                minDateTime={customStartDate || new Date()}
                            />
                        </Stack>
                    )}
                </Box>

                {/* ─── Duration Card ─── */}
                <Box sx={sectionCardStyle}>
                    <Typography sx={sectionLabelStyle}>
                        <Iconify icon='mdi:timer-outline' width={15} sx={{ color: '#5C6BC0' }} />
                        Appointment Duration
                    </Typography>
                    <Box sx={presetChipGroupStyle}>
                        {DURATION_PRESETS.map(({ key, label }) => (
                            <Chip
                                key={key}
                                label={label}
                                size='small'
                                variant={durationPreset === key ? 'filled' : 'outlined'}
                                onClick={() => setDurationPreset(key)}
                                sx={presetChipStyle(durationPreset === key)}
                            />
                        ))}
                    </Box>
                    {durationPreset === 'custom' && (
                        <TextField
                            size='small'
                            label='Duration (minutes)'
                            type='number'
                            value={customDuration}
                            onChange={(e) => setCustomDuration(e.target.value)}
                            sx={{
                                mt: 1.5,
                                width: 180,
                                '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                            }}
                            inputProps={{ min: 5, max: 480 }}
                        />
                    )}
                </Box>

                {/* ─── Search Button ─── */}
                <LoadingButton
                    fullWidth
                    variant='contained'
                    loading={isSearching}
                    onClick={handleSearch}
                    disabled={!canSearch}
                    startIcon={<Iconify icon='mdi:magnify' width={20} />}
                    sx={{ ...searchButtonStyle, mb: 2 }}
                >
                    {isSearching
                        ? `Searching ${searchProgress.done}/${searchProgress.total}...`
                        : 'Search Availability'
                    }
                </LoadingButton>

                {/* Progress bar during parallel fetches */}
                {isSearching && searchProgress.total > 1 && (
                    <LinearProgress
                        variant='determinate'
                        value={progressPercent}
                        sx={searchProgressBarStyle}
                    />
                )}

                {/* ─── Results ─── */}
                {hasSearched && (
                    <>
                        <Box sx={resultsHeaderStyle}>
                            <Stack direction='row' alignItems='center' justifyContent='space-between'>
                                <Stack direction='row' alignItems='center' spacing={1}>
                                    <Iconify
                                        icon={results.length > 0 ? 'mdi:calendar-check' : 'mdi:calendar-alert'}
                                        width={18}
                                        sx={{ color: results.length > 0 ? 'success.main' : 'text.disabled' }}
                                    />
                                    <Typography variant='subtitle2' sx={{ fontWeight: 700, fontSize: 13 }}>
                                        {(() => {
                                            if (results.length > 0) return `${results.length} slot${results.length > 1 ? 's' : ''} available`;
                                            if (isSearching) return 'Searching...';
                                            return 'No slots found';
                                        })()}
                                    </Typography>
                                </Stack>
                                {results.length > 0 && (
                                    <Chip
                                        label='Clear results'
                                        size='small'
                                        variant='outlined'
                                        onClick={() => { setResults([]); setHasSearched(false); setSelectedSlot(null); }}
                                        deleteIcon={<Iconify icon='mdi:close' width={14} />}
                                        onDelete={() => { setResults([]); setHasSearched(false); setSelectedSlot(null); }}
                                        sx={{ fontSize: 11, height: 24, borderRadius: '6px' }}
                                    />
                                )}
                            </Stack>
                        </Box>
                        <DentallyAvailabilityResults
                            results={results}
                            onSelect={handleSlotSelect}
                            practitioners={allPractitioners}
                            selectedSlot={selectedSlot}
                            loading={isSearching}
                        />
                    </>
                )}
            </Box>

            {/* ─── Footer — Confirm selection ─── */}
            {selectedSlot && (
                <Box sx={drawerFooterStyle}>
                    <Box sx={footerSummaryStyle}>
                        <Box sx={{
                            width: 32, height: 32, borderRadius: '50%',
                            bgcolor: 'rgba(34,197,94,0.12)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                            <Iconify icon='mdi:check-circle' width={18} sx={{ color: 'success.main' }} />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant='body2' noWrap sx={{ fontWeight: 700, fontSize: 13 }}>
                                {selectedSlot.practitioner_name}
                            </Typography>
                            <Typography variant='caption' sx={{ color: 'text.secondary', fontSize: 11 }}>
                                {(parseDentallyDate(selectedSlot.start_time) || new Date(selectedSlot.start_time)).toLocaleString('en-GB', {
                                    weekday: 'short', day: 'numeric', month: 'short',
                                    hour: '2-digit', minute: '2-digit', hour12: false,
                                })}
                            </Typography>
                        </Box>
                    </Box>
                    <FormControl fullWidth size='small' sx={{ mb: 1.5 }}>
                        <InputLabel id='dentally-reason-label' sx={{ fontSize: 13 }}>Appointment Reason</InputLabel>
                        <Select
                            labelId='dentally-reason-label'
                            value={appointmentReason}
                            label='Appointment Reason'
                            onChange={(e) => setAppointmentReason(e.target.value)}
                            sx={{ fontSize: 13, borderRadius: '8px' }}
                        >
                            {reasonOptions.map((r) => (
                                <MenuItem key={r} value={r} sx={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Iconify icon='mdi:calendar-clock-outline' width={14} sx={{ color: reasonTreatmentMap[r] ? '#855CF8' : '#919EAB', flexShrink: 0 }} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant='body2' component='span' sx={{ fontSize: 13 }}>{r}</Typography>
                                        {reasonTreatmentMap[r] && (
                                            <Typography variant='caption' color='text.secondary' component='span' sx={{ ml: 0.5 }}>
                                                → {reasonTreatmentMap[r]}
                                            </Typography>
                                        )}
                                    </Box>
                                    {reasonTreatmentMap[r] && <Chip size='small' label='Linked' color='success' variant='outlined' sx={{ fontSize: 9, height: 16 }} />}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        fullWidth
                        variant='contained'
                        onClick={handleConfirmSelection}
                        startIcon={<Iconify icon='mdi:check-bold' width={18} />}
                        sx={confirmButtonStyle}
                    >
                        Confirm & Fill Details
                    </Button>
                </Box>
            )}
        </Drawer>
    );
};

DentallyAvailabilityDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default DentallyAvailabilityDrawer;
